import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import menuTopSvg from "@/assets/brand/menu-top.svg?url";
import menuBodySvg from "@/assets/brand/menu-body.svg?url";
import menuLineSvg from "@/assets/brand/line.svg?url";
import menuFooterSvg from "@/assets/brand/menu-footer.svg?url";

export interface MenuItem {
  title: string;
  path?: string;
  icon: string;
  key?: string;
  children?: MenuItem[];
}

interface AppSidebarProps {
  isSidebarCollapsed: boolean;
  menuItems: MenuItem[];
}

export function AppSidebar({ isSidebarCollapsed, menuItems }: AppSidebarProps) {
  const location = useLocation();
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const toggleCategory = (key: string) => {
    setExpandedCategory(prev => prev === key ? null : key);
  };

  return (
    <aside
      className="sidebar-scroll app-sidebar-container"
      style={{
        width: isSidebarCollapsed ? "70px" : "280px",
        backgroundColor: "var(--color-sidebar)",
        backgroundImage: `url('${menuFooterSvg}'), url('${menuBodySvg}')`,
        backgroundPosition: "bottom center, top center",
        backgroundRepeat: "no-repeat, repeat-y",
        backgroundSize: "100% auto, 100% auto",
        borderLeft: "1px solid var(--glass-border)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "100vh",
        transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden",
        padding: 0,
        boxSizing: "border-box",
        flexShrink: 0,
        order: 1, // Place sidebar on the right for RTL
        direction: "rtl",
        position: "relative",
        fontFamily: "var(--font-family-fa)",
      }}
    >
      {/* Top SVG Section */}
      <div style={{ position: "relative", width: "100%", flexShrink: 0 }}>
        <img
          src={menuTopSvg}
          alt="Menu Top"
          style={{ width: "100%", height: "auto", display: "block" }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "8%",
            left: 0,
            right: 0,
            textAlign: "center",
            fontWeight: "bold",
            color: "var(--color-surface)",
            fontSize: isSidebarCollapsed ? "0.75rem" : "1.2rem",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            padding: "0 10px",
            fontFamily: "var(--font-family-fa)",
          }}
        >
          <Link to="/" style={{ color: "var(--color-surface)", textDecoration: "none" }}>
            {isSidebarCollapsed ? "داشبورد" : "داشبورد اصلی"}
          </Link>
        </div>
      </div>

      {/* Content wrapper with transparent background allowing aside background art to show */}
      <div
        className="sidebar-scroll"
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "0rem",
          background: "transparent",
          padding: "0.2rem 0.4rem 4.5rem 0.4rem",
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
        }}
      >
        {menuItems.map((item, index) => {
          const hasChildren = item.children && item.children.length > 0;
          const isExpanded = item.key ? expandedCategory === item.key : false;

          return (
            <div key={item.key || index} style={{ display: "flex", flexDirection: "column" }}>
              {index > 0 && (
                <div style={{ padding: "0", margin: "1px 0", height: "3px", display: "flex", justifyContent: "center", alignItems: "center", overflow: "hidden", zIndex: 0 }}>
                  <img
                    src={menuLineSvg}
                    alt="Separator"
                    style={{ width: "35%", height: "2px", opacity: 0.4, display: "block", objectFit: "contain" }}
                  />
                </div>
              )}
              {hasChildren && item.key ? (
                <>
                  <div
                    className="tree-header"
                    onClick={() => item.key && toggleCategory(item.key)}
                    style={{
                      justifyContent: isSidebarCollapsed ? "center" : "space-between",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span style={{ fontSize: "1.1rem" }}>{item.icon}</span>
                      {!isSidebarCollapsed && <span style={{ fontSize: "0.9rem", fontWeight: "600" }}>{item.title}</span>}
                    </div>
                    {!isSidebarCollapsed && (
                      <span style={{ fontSize: "0.7rem", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0)" }}>
                        ▼
                      </span>
                    )}
                  </div>

                  {isExpanded && !isSidebarCollapsed && (
                    <div style={{ paddingRight: "1.2rem", display: "flex", flexDirection: "column", borderRight: "1px solid rgba(255,255,255,0.05)", marginRight: "0.8rem", marginTop: "0.15rem" }}>
                      {item.children?.map((child, cIdx) => {
                        const isActive = location.pathname === child.path;
                        return (
                          <Link
                            key={cIdx}
                            to={child.path ?? "#"}
                            className={`nav-link ${isActive ? "active" : ""}`}
                            style={{ fontSize: "0.82rem" }}
                          >
                            <span>{child.icon}</span>
                            <span>{child.title}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.path ?? "#"}
                  className={`nav-link ${location.pathname === item.path ? "active" : ""}`}
                  style={{
                    justifyContent: isSidebarCollapsed ? "center" : "flex-start",
                  }}
                >
                  <span style={{ fontSize: "1.1rem" }}>{item.icon}</span>
                  {!isSidebarCollapsed && <span style={{ fontSize: "0.9rem", fontWeight: "600" }}>{item.title}</span>}
                </Link>
              )}
            </div>
          );
        })}
      </div>

      {/* Adding Media Query for Mobile to handle drawer visibility via CSS */}
      <style>{`
        @media (max-width: 768px) {
          .app-sidebar-container {
            width: 100vw !important;
            max-width: 360px;
            position: absolute;
            height: 100%;
            z-index: 50;
            transform: translateX(${isSidebarCollapsed ? '100%' : '0'});
          }
        }
      `}</style>
    </aside>
  );
}
